import { ApiError } from '@/lib/api-client.js';
import type { BuildCheckoutFailure } from '@/lib/build-checkout-payload.js';

// ── checkout errors (shared by marketplace payment + outreach) ───────────────

export interface ErrorCopy {
  title: string;
  message: string;
}

/** Why `buildCheckoutPayload` refused to assemble a request. */
export function checkoutPayloadErrorCopy(reason: BuildCheckoutFailure): ErrorCopy {
  switch (reason) {
    case 'stale_cart':
      return {
        title: 'Some plans are no longer available',
        message: 'Pick a new plan to continue.',
      };
    case 'empty_cart':
      return {
        title: 'Your selection is empty',
        message: 'Pick a plan to continue.',
      };
    case 'missing_financing':
      return {
        title: "We couldn't read your financing terms",
        message: 'Check your term length and down payment, then try again.',
      };
  }
}

// ── rating errors (shared by marketplace entry/tiers + outreach) ─────────────

/** The sibling of `checkoutErrorCopy`, for `/api/rate` failures. */
export function rateErrorCopy(err: unknown): ErrorCopy {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'NETWORK':
        return {
          title: "We couldn't connect",
          message: 'Check your connection and try again.',
        };
      case 'RATE_LIMITED':
        return {
          title: "You're going too fast",
          message: 'Wait a moment and try again.',
        };
      case 'INVALID_WIDGET_API_KEY':
        return {
          title: "This widget isn't fully set up",
          message: 'Please contact your dealer — they need to finish the integration.',
        };
      case 'BAD_REQUEST':
        return {
          title: "We couldn't look up that vehicle",
          message:
            err.message.length > 0
              ? err.message
              : 'Double-check the details you entered and try again.',
        };
    }
  }
  return {
    title: 'Something went wrong',
    message: 'Please try again. If this keeps happening, contact your dealer.',
  };
}

/** Callers that special-case a code (e.g. STALE_RATE) handle it before calling this. */
export function checkoutErrorCopy(err: unknown): ErrorCopy {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'STALE_RATE':
        return {
          title: 'Your quote has expired',
          message: 'Reload the link to get fresh pricing.',
        };
      case 'QUOTE_ALREADY_CONVERTED':
        return {
          title: 'This checkout is already complete',
          message: 'Look for an email from DocuSign in the next few minutes to sign your contract.',
        };
      case 'RATE_LIMITED':
        return {
          title: "You're going too fast",
          message: 'Wait a moment and try again.',
        };
      case 'BAD_REQUEST':
        return {
          title: "We couldn't process your details",
          message:
            err.message.length > 0
              ? err.message
              : 'Some information is missing or invalid — go back and check your entries.',
        };
    }
  }
  return {
    title: "We couldn't reach our payment processor",
    message: 'Please try again. If this keeps happening, contact your dealer.',
  };
}

/** Flattens copy to one sentence for the outreach chat's inline error chip. */
export function toLine(copy: ErrorCopy): string {
  return `${copy.title}. ${copy.message}`;
}

// ── app boot errors (config gate in app.tsx) ─────────────────────────────────

export type BootErrorKind = 'network' | 'auth' | 'unknown';

export function bootErrorKind(err: unknown): BootErrorKind {
  if (err instanceof ApiError) {
    if (err.code === 'NETWORK') return 'network';
    if (err.status === 401 || err.status === 403) return 'auth';
  }
  return 'unknown';
}

export interface RetryableCopy extends ErrorCopy {
  /** `false` where retrying can't help (a bad key), so the caller hides its retry. */
  retryable: boolean;
}

export function bootErrorCopy(kind: BootErrorKind): RetryableCopy {
  switch (kind) {
    case 'auth':
      return {
        title: 'Widget unavailable',
        message: "This widget isn't set up correctly. Please reach out to the dealer.",
        retryable: false,
      };
    case 'network':
      return {
        title: "We couldn't connect",
        message: 'Check your connection.',
        retryable: true,
      };
    default:
      return {
        title: 'Something went wrong',
        message: "We couldn't load the widget.",
        retryable: true,
      };
  }
}

// ── outreach seed errors (chat bubble copy) ──────────────────────────────────

export type SeedErrorKind = 'expired' | 'purchased' | 'network' | 'unknown';

export function seedErrorKind(err: unknown): SeedErrorKind {
  if (err instanceof ApiError) {
    if (err.code === 'OUTREACH_ALREADY_PURCHASED') return 'purchased';
    if (err.code === 'EXPIRED_OR_REVOKED' || err.status === 410) return 'expired';
    if (err.code === 'NETWORK') return 'network';
  }
  return 'unknown';
}

export interface ChatErrorCopy {
  /** Conversational dealer-voice line rendered as a chat bubble. */
  message: string;
  retryable: boolean;
}

export function seedErrorCopy(kind: SeedErrorKind): ChatErrorCopy {
  switch (kind) {
    case 'expired':
      return {
        message:
          "Sorry — this follow-up link is no longer active. Reach out to us and we'll pick up where you left off.",
        retryable: false,
      };
    case 'purchased':
      return {
        message:
          "You're all set — this coverage is already purchased. Look for an email from DocuSign to sign your contract.",
        retryable: false,
      };
    case 'network':
      return {
        message: "Hmm, I couldn't connect just now. Check your connection and try again.",
        retryable: true,
      };
    default:
      return {
        message: 'Something went wrong opening this follow-up. Want to try again?',
        retryable: true,
      };
  }
}

/** Keeps a fatal error in the chat voice rather than a generic card. */
export const OUTREACH_FATAL_MESSAGE =
  'Something went wrong on our end. Try again, or reach out using the details below.';
